import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageAds = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [ads, setAds] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [sponsors, setSponsors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    // Modal state for overriding and approving
    const [activeAd, setActiveAd] = useState(null);
    const [cpcRate, setCpcRate] = useState(10);
    const [cpmRate, setCpmRate] = useState(50);
    const [overrideRestId, setOverrideRestId] = useState('');
    const [overrideType, setOverrideType] = useState('Include');
    const [runningLocations, setRunningLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);

    // Create ad modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [adForm, setAdForm] = useState({
        sponsor_id: '',
        title: '',
        target_url: '',
        main_category_id: '',
        sub_category_id: '',
        target_state_ids: [],
        target_city_ids: [],
        target_pincode: '',
        placement_zone: 'in_menu_first',
        cpc_rate: '10',
        cpm_rate: '50',
        status: 'Active',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [formError, setFormError] = useState('');
    const [createLoading, setCreateLoading] = useState(false);
    const [editAdId, setEditAdId] = useState(null);

    // Create sponsor modal state
    const [showSponsorModal, setShowSponsorModal] = useState(false);
    const [sponsorForm, setSponsorForm] = useState({
        company_name: '',
        contact_name: '',
        email: '',
        password: '',
        phone: ''
    });
    const [sponsorError, setSponsorError] = useState('');
    const [sponsorLoading, setSponsorLoading] = useState(false);

    const fetchAds = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'fetch_ads');
            if (statusFilter) fd.append('status', statusFilter);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setAds(data.ads || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchAds(false);
        fetchRestaurants();
        fetchSponsors();
        fetchCategories();
        fetchStates();

        const pollInterval = setInterval(() => {
            fetchAds(true);
        }, 10000);

        return () => clearInterval(pollInterval);
        // eslint-disable-next-line
    }, [statusFilter]);

    useEffect(() => {
        if (activeAd) {
            fetchRunningLocations(activeAd.id);
        } else {
            setRunningLocations([]);
        }
        // eslint-disable-next-line
    }, [activeAd]);

    const fetchRunningLocations = async (adId) => {
        setLocationsLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'get_ad_locations');
            fd.append('ad_id', adId);
            const res = await fetch(`${BASE}/apimenu/v2/get_ad_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setRunningLocations(data.locations || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLocationsLoading(false);
        }
    };

    const fetchRestaurants = async () => {
        try {
            const fd = new FormData();
            fd.append('action', 'fetch_restaurants');
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) setRestaurants(data.restaurants || []);
        } catch (err) {}
    };

    const fetchSponsors = async () => {
        try {
            const fd = new FormData();
            fd.append('action', 'fetch_sponsors');
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) setSponsors(data.sponsors || []);
        } catch (err) {}
    };

    const fetchCategories = async () => {
        try {
            const fd = new FormData();
            fd.append('action', 'get_categories');
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) setCategories(data.categories || []);
        } catch (err) {}
    };

    const fetchStates = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/get_locations.php?action=get_states`);
            const data = await res.json();
            if (data.success) setStates(data.states || []);
        } catch (err) {}
    };

    const handleStateChange = async (stateIds) => {
        setAdForm(prev => ({ ...prev, target_state_ids: stateIds, target_city_ids: [] }));
        setCities([]);
        if (!stateIds || stateIds.length === 0) return;
        try {
            const res = await fetch(`${BASE}/apimenu/get_locations.php?action=get_cities&state_ids=${stateIds.join(',')}`);
            const data = await res.json();
            if (data.success) setCities(data.cities || []);
        } catch (err) {}
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAdForm(prev => ({ ...prev, image: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleApprove = async () => {
        if (!activeAd) return;
        try {
            const fd = new FormData();
            fd.append('action', 'approve_ad');
            fd.append('ad_id', activeAd.id);
            fd.append('cpc_rate', cpcRate);
            fd.append('cpm_rate', cpmRate);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setActiveAd(null);
                fetchAds();
            } else {
                await customAlert(data.message || 'Error approving', 'Error', 'danger');
            }
        } catch (err) {}
    };

    const handleReject = async (adId) => {
        if (!await customConfirm('Reject this ad?', 'Reject Campaign', 'warning')) return;
        try {
            const fd = new FormData();
            fd.append('action', 'reject_ad');
            fd.append('ad_id', adId);
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) fetchAds();
        } catch (err) {}
    };

    const handleAddOverride = async () => {
        if (!activeAd || !overrideRestId) return;
        try {
            const fd = new FormData();
            fd.append('action', 'update_overrides');
            fd.append('ad_id', activeAd.id);
            fd.append('restaurant_id', overrideRestId);
            fd.append('override_type', overrideType);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setOverrideRestId('');
                fetchAds();
                const newOverride = {
                    id: Date.now(),
                    restaurant_id: overrideRestId,
                    override_type: overrideType,
                    restaurant_name: restaurants.find(r => r.id.toString() === overrideRestId)?.restaurant_name
                };
                setActiveAd(prev => ({...prev, overrides: [...prev.overrides.filter(o => o.restaurant_id.toString() !== overrideRestId), newOverride]}));
            }
        } catch (err) {}
    };

    const handleRemoveOverride = async (restId) => {
        if (!activeAd) return;
        try {
            const fd = new FormData();
            fd.append('action', 'update_overrides');
            fd.append('ad_id', activeAd.id);
            fd.append('restaurant_id', restId);
            fd.append('override_type', 'Remove');

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchAds();
                setActiveAd(prev => ({...prev, overrides: prev.overrides.filter(o => o.restaurant_id.toString() !== restId.toString())}));
            }
        } catch (err) {}
    };

    const handleCreateSponsorSubmit = async (e) => {
        e.preventDefault();
        setSponsorError('');
        if (!sponsorForm.company_name || !sponsorForm.email || !sponsorForm.password) {
            setSponsorError('Company Name, Email, and Password are required.');
            return;
        }

        setSponsorLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'create_sponsor');
            fd.append('company_name', sponsorForm.company_name);
            fd.append('contact_name', sponsorForm.contact_name);
            fd.append('email', sponsorForm.email);
            fd.append('password', sponsorForm.password);
            fd.append('phone', sponsorForm.phone);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Sponsor created successfully!', 'Success', 'success');
                setShowSponsorModal(false);
                setSponsorForm({
                    company_name: '',
                    contact_name: '',
                    email: '',
                    password: '',
                    phone: ''
                });
                fetchSponsors();
            } else {
                setSponsorError(data.message || 'Error creating sponsor');
            }
        } catch (err) {
            setSponsorError('Network error, please try again.');
        } finally {
            setSponsorLoading(false);
        }
    };

    const handleCreateCampaignSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (!adForm.sponsor_id || !adForm.title || !adForm.target_url || !adForm.main_category_id) {
            setFormError('Please fill all required fields.');
            return;
        }

        if (!editAdId && !adForm.image) {
            setFormError('Ad image is required for new campaigns.');
            return;
        }

        setCreateLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', editAdId ? 'update_ad' : 'create_ad');
            if (editAdId) fd.append('ad_id', editAdId);
            
            fd.append('sponsor_id', adForm.sponsor_id);
            fd.append('title', adForm.title);
            fd.append('target_url', adForm.target_url);
            fd.append('main_category_id', adForm.main_category_id);
            fd.append('sub_category_id', adForm.sub_category_id || 0);
            fd.append('target_state_ids', JSON.stringify(adForm.target_state_ids));
            fd.append('target_city_ids', JSON.stringify(adForm.target_city_ids));
            fd.append('target_pincode', adForm.target_pincode);
            fd.append('placement_zone', adForm.placement_zone);
            fd.append('cpc_rate', adForm.cpc_rate);
            fd.append('cpm_rate', adForm.cpm_rate);
            fd.append('status', adForm.status);
            
            if (adForm.image && typeof adForm.image !== 'string') {
                fd.append('image', adForm.image);
            }

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert(editAdId ? 'Campaign updated successfully!' : 'Campaign created successfully!', 'Success', 'success');
                setShowCreateModal(false);
                setEditAdId(null);
                setAdForm({
                    sponsor_id: '', title: '', target_url: '', main_category_id: '', sub_category_id: '',
                    target_state_ids: [], target_city_ids: [], target_pincode: '', placement_zone: 'in_menu_first', cpc_rate: '10', cpm_rate: '50', status: 'Active', image: null
                });
                setImagePreview(null);
                fetchAds();
            } else {
                setFormError(data.message || 'Error saving campaign');
            }
        } catch (err) {
            setFormError('Network error occurred.');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditClick = (ad) => {
        setEditAdId(ad.id);
        setAdForm({
            sponsor_id: ad.sponsor_id,
            title: ad.title,
            target_url: ad.target_url,
            main_category_id: ad.main_category_id || '',
            sub_category_id: ad.sub_category_id || '',
            target_state_ids: ad.target_state_ids ? JSON.parse(ad.target_state_ids) : [],
            target_city_ids: ad.target_city_ids ? JSON.parse(ad.target_city_ids) : [],
            target_pincode: ad.target_pincode || '',
            placement_zone: ad.placement_zone || 'in_menu_first',
            cpc_rate: ad.cpc_rate || '10',
            cpm_rate: ad.cpm_rate || '50',
            status: ad.status || 'Active',
            image: ad.image_url
        });
        setImagePreview(ad.image_url);
        setShowCreateModal(true);
    };

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
                
                {/* Header Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h2 className="sa-page-title">Ad Campaigns Management</h2>
                        <p className="sa-page-subtitle">Review, approve, and manage sponsor ad proposals across the network.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={() => setShowSponsorModal(true)} 
                            className="sa-secondary-btn" 
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1.2rem', fontSize: '0.9rem' }}
                        >
                            <i className="fas fa-user-plus"></i> Add Sponsor
                        </button>
                        <button className="sa-primary-btn" onClick={() => {
                                setEditAdId(null);
                                setAdForm({
                                    sponsor_id: '', title: '', target_url: '', main_category_id: '', sub_category_id: '',
                                    target_state_ids: [], target_city_ids: [], target_pincode: '', placement_zone: 'in_menu_first', cpc_rate: '10', cpm_rate: '50', status: 'Active', image: null
                                });
                                setImagePreview(null);
                                setShowCreateModal(true);
                            }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1.2rem', fontSize: '0.9rem' }}>
                                <i className="fas fa-plus"></i> Create New Campaign
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="sa-panel" style={{ display: 'flex', gap: '15px', marginBottom: '1.5rem', padding: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status Filter:</label>
                        <select 
                            className="sa-select" 
                            value={statusFilter} 
                            onChange={e => setStatusFilter(e.target.value)} 
                            style={{ minWidth: '180px', padding: '8px 12px' }}
                        >
                            <option value="">All Statuses</option>
                            <option value="Pending_Approval">Pending Approval</option>
                            <option value="Active">Active</option>
                            <option value="Paused">Paused</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                {/* Main Table */}
                <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                        <table className="sa-table" style={{ margin: 0 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '130px' }}>Banner</th>
                                    <th>Sponsor & Title</th>
                                    <th>Targeting</th>
                                    <th>Status & Perf</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: 'var(--accent-primary)', marginBottom: '0.5rem' }}></i>
                                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Loading campaigns...</p>
                                        </td>
                                    </tr>
                                ) : ads.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                                            No campaigns found.
                                        </td>
                                    </tr>
                                ) : (
                                    ads.map(ad => {
                                        const statusColor = 
                                            ad.status === 'Active' ? 'rgba(34, 197, 94, 0.1)' :
                                            ad.status === 'Pending_Approval' ? 'rgba(234, 179, 8, 0.1)' :
                                            ad.status === 'Rejected' ? 'rgba(239, 68, 68, 0.1)' :
                                            'rgba(107, 114, 128, 0.1)';

                                        const textStatusColor = 
                                            ad.status === 'Active' ? 'var(--success)' :
                                            ad.status === 'Pending_Approval' ? 'var(--warning)' :
                                            ad.status === 'Rejected' ? 'var(--danger)' :
                                            'var(--text-secondary)';

                                        return (
                                            <tr key={ad.id}>
                                                <td>
                                                    <a href={ad.target_url} target="_blank" rel="noreferrer">
                                                        <img 
                                                            src={ad.image_url} 
                                                            alt="Ad Banner" 
                                                            style={{ width: '110px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-color)' }} 
                                                        />
                                                    </a>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{ad.title}</div>
                                                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '2px' }}>
                                                        <i className="fas fa-building" style={{ marginRight: '5px' }}></i>{ad.sponsor_name}
                                                    </div>
                                                    <span style={{ display: 'inline-block', fontSize: '0.75rem', background: 'var(--bg-dark)', padding: '2px 6px', borderRadius: '4px', marginTop: '5px', color: 'var(--text-secondary)' }}>
                                                        {ad.main_cat} {ad.sub_cat ? `> ${ad.sub_cat}` : ''}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                                                        <i className="fas fa-map-marker-alt" style={{ color: 'var(--accent-primary)', marginRight: '6px' }}></i>
                                                        {(() => {
                                                            const sList = ad.state_name ? ad.state_name.split(', ') : [];
                                                            if (sList.length === 0) return 'Global (All Locations)';
                                                            if (sList.length === 1) return ad.city_name ? `${ad.city_name}, ${sList[0]}` : sList[0];
                                                            return `Multiple Regions (${sList.length})`;
                                                        })()}
                                                    </div>
                                                    {ad.target_pincode && (
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '3px' }}>
                                                            PIN: {ad.target_pincode}
                                                        </div>
                                                    )}
                                                    {ad.overrides && ad.overrides.length > 0 && (
                                                        <div style={{ marginTop: '5px', color: '#ea580c', fontWeight: 600, fontSize: '0.8rem' }}>
                                                            <i className="fas fa-sliders-h" style={{ marginRight: '5px' }}></i>{ad.overrides.length} Overrides
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span style={{ 
                                                        display: 'inline-block', padding: '3px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                                                        background: statusColor, color: textStatusColor
                                                    }}>
                                                        {ad.status.replace('_', ' ')}
                                                    </span>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                                                        <strong>{ad.total_impressions}</strong> views • <strong>{ad.total_clicks}</strong> clicks
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                                        Rate: ₹{ad.cpc_rate}/click | ₹{ad.cpm_rate}/1k views
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                        <button 
                                                            onClick={() => handleEditClick(ad)}
                                                            className="sa-action-btn secondary"
                                                            title="Edit Details"
                                                        >
                                                            <i className="fas fa-edit"></i> Edit
                                                        </button>
                                                        <button 
                                                            onClick={() => {
                                                                setActiveAd(ad);
                                                                setCpcRate(ad.cpc_rate || 10);
                                                                setCpmRate(ad.cpm_rate || 50);
                                                            }} 
                                                            className="sa-action-btn primary"
                                                            title="Update Rates & Overrides"
                                                        >
                                                            <i className="fas fa-cog"></i> Settings
                                                        </button>
                                                        {ad.status === 'Pending_Approval' && (
                                                            <button 
                                                                onClick={() => handleReject(ad.id)} 
                                                                className="sa-action-btn danger"
                                                            >
                                                                <i className="fas fa-times"></i> Reject
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal for Creating Campaign */}
                {showCreateModal && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '750px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="sa-modal-header">
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-bullhorn" style={{ color: 'var(--accent-primary)', marginRight: '10px' }}></i>{editAdId ? 'Edit Campaign' : 'Create New Campaign'}</h3>
                                <button className="sa-modal-close" onClick={() => setShowCreateModal(false)}><i className="fas fa-times"></i></button>
                            </div>

                            {formError && (
                                <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', padding: '10px 15px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="fas fa-exclamation-circle"></i> {formError}
                                </div>
                            )}

                            <form onSubmit={handleCreateCampaignSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">Select Sponsor Account *</label>
                                        <select 
                                            className="sa-select" 
                                            required 
                                            value={adForm.sponsor_id} 
                                            onChange={e => setAdForm(prev => ({ ...prev, sponsor_id: e.target.value }))}
                                        >
                                            <option value="">-- Choose Sponsor --</option>
                                            {sponsors.map(sp => (
                                                <option key={sp.id} value={sp.id}>{sp.company_name} ({sp.email})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Campaign Title *</label>
                                        <input 
                                            type="text" 
                                            className="sa-input" 
                                            required 
                                            placeholder="e.g. Diwali Dhamaka Offer" 
                                            value={adForm.title} 
                                            onChange={e => setAdForm(prev => ({ ...prev, title: e.target.value }))} 
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Target URL (Link) *</label>
                                        <input 
                                            type="url" 
                                            className="sa-input" 
                                            required 
                                            placeholder="https://www.tata.com/offers" 
                                            value={adForm.target_url} 
                                            onChange={e => setAdForm(prev => ({ ...prev, target_url: e.target.value }))} 
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Main Category *</label>
                                        <select 
                                            className="sa-select" 
                                            required 
                                            value={adForm.main_category_id} 
                                            onChange={e => setAdForm(prev => ({ ...prev, main_category_id: e.target.value, sub_category_id: '' }))}
                                        >
                                            <option value="">-- Choose Category --</option>
                                            {categories.filter(c => !c.parent_id || c.parent_id === '0' || c.parent_id === 0 || c.parent_id === 'null').map(c => (
                                                <option key={c.id} value={c.id}>{c.category_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Sub Category</label>
                                        <select 
                                            className="sa-select" 
                                            value={adForm.sub_category_id} 
                                            onChange={e => setAdForm(prev => ({ ...prev, sub_category_id: e.target.value }))}
                                        >
                                            <option value="">-- All Subcategories --</option>
                                            {categories.filter(c => c.parent_id && c.parent_id !== '0' && c.parent_id !== 0 && c.parent_id !== 'null' && adForm.main_category_id && c.parent_id.toString() === adForm.main_category_id.toString()).map(c => (
                                                <option key={c.id} value={c.id}>{c.category_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Target States (Multi-select)</label>
                                        <select 
                                            multiple
                                            className="sa-select" 
                                            style={{ height: '100px' }}
                                            value={adForm.target_state_ids} 
                                            onChange={e => handleStateChange(Array.from(e.target.selectedOptions, opt => opt.value))}
                                        >
                                            {states.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Target Cities (Multi-select)</label>
                                        <select 
                                            multiple
                                            className="sa-select" 
                                            style={{ height: '100px' }}
                                            value={adForm.target_city_ids} 
                                            onChange={e => setAdForm(prev => ({ ...prev, target_city_ids: Array.from(e.target.selectedOptions, opt => opt.value) }))}
                                            disabled={!adForm.target_state_ids || adForm.target_state_ids.length === 0}
                                        >
                                            {cities.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Target Pincode (Optional)</label>
                                        <input 
                                            type="text" 
                                            className="sa-input" 
                                            placeholder="e.g. 305001" 
                                            value={adForm.target_pincode} 
                                            onChange={e => setAdForm(prev => ({ ...prev, target_pincode: e.target.value }))} 
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Placement Zone</label>
                                        <select 
                                            className="sa-select" 
                                            value={adForm.placement_zone} 
                                            onChange={e => setAdForm(prev => ({ ...prev, placement_zone: e.target.value }))}
                                        >
                                            <option value="in_menu_first">In-Menu (First Slot)</option>
                                            <option value="in_menu_second">In-Menu (Second Slot)</option>
                                        </select>
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Cost Per Click (CPC) Rate (₹) *</label>
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            className="sa-input" 
                                            required 
                                            value={adForm.cpc_rate} 
                                            onChange={e => setAdForm(prev => ({ ...prev, cpc_rate: e.target.value }))} 
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Cost Per 1k Views (CPM) Rate (₹) *</label>
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            className="sa-input" 
                                            required 
                                            value={adForm.cpm_rate} 
                                            onChange={e => setAdForm(prev => ({ ...prev, cpm_rate: e.target.value }))} 
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Initial Status</label>
                                        <select 
                                            className="sa-select" 
                                            value={adForm.status} 
                                            onChange={e => setAdForm(prev => ({ ...prev, status: e.target.value }))}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Paused">Paused</option>
                                        </select>
                                    </div>

                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">Upload Ad Banner {editAdId && '(Leave empty to keep existing)'}</label>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={handleFileChange} 
                                                className="sa-input" 
                                                style={{ flex: 1 }}
                                            />
                                            {imagePreview && (
                                                <img src={imagePreview} alt="preview" style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowCreateModal(false)} 
                                        className="sa-secondary-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={createLoading} 
                                        className="sa-primary-btn"
                                    >
                                        {createLoading ? 'Saving...' : (editAdId ? 'Update Campaign' : 'Publish Campaign')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal for Creating Sponsor */}
                {showSponsorModal && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="sa-modal-header">
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-user-plus" style={{ color: 'var(--accent-primary)', marginRight: '10px' }}></i>Add New Sponsor</h3>
                                <button className="sa-modal-close" onClick={() => setShowSponsorModal(false)}><i className="fas fa-times"></i></button>
                            </div>

                            {sponsorError && (
                                <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', padding: '10px 15px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="fas fa-exclamation-circle"></i> {sponsorError}
                                </div>
                            )}

                            <form onSubmit={handleCreateSponsorSubmit}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Company Name *</label>
                                        <input 
                                            type="text" 
                                            className="sa-input" 
                                            required 
                                            placeholder="e.g. Tata Motors" 
                                            value={sponsorForm.company_name} 
                                            onChange={e => setSponsorForm(prev => ({ ...prev, company_name: e.target.value }))} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Contact Person Name</label>
                                        <input 
                                            type="text" 
                                            className="sa-input" 
                                            placeholder="e.g. Rajesh Kumar" 
                                            value={sponsorForm.contact_name} 
                                            onChange={e => setSponsorForm(prev => ({ ...prev, contact_name: e.target.value }))} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Email Address *</label>
                                        <input 
                                            type="email" 
                                            className="sa-input" 
                                            required 
                                            placeholder="sponsor@domain.in" 
                                            value={sponsorForm.email} 
                                            onChange={e => setSponsorForm(prev => ({ ...prev, email: e.target.value }))} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Password *</label>
                                        <input 
                                            type="password" 
                                            className="sa-input" 
                                            required 
                                            placeholder="Choose a strong password" 
                                            value={sponsorForm.password} 
                                            onChange={e => setSponsorForm(prev => ({ ...prev, password: e.target.value }))} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Phone Number</label>
                                        <input 
                                            type="text" 
                                            className="sa-input" 
                                            placeholder="e.g. +91 9876543210" 
                                            value={sponsorForm.phone} 
                                            onChange={e => setSponsorForm(prev => ({ ...prev, phone: e.target.value }))} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowSponsorModal(false)} 
                                        className="sa-secondary-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={sponsorLoading} 
                                        className="sa-primary-btn"
                                    >
                                        {sponsorLoading ? 'Saving...' : 'Add Sponsor'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal for Approving / Overrides */}
                {activeAd && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-sliders-h" style={{ color: 'var(--accent-primary)', marginRight: '10px' }}></i>Manage Ad: {activeAd.title}</h3>
                                <button onClick={() => setActiveAd(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}><i className="fas fa-times"></i></button>
                            </div>
                            
                            <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--bg-dark)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '1rem' }}>Targeting Overrides</h4>
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '15px', lineHeight: '1.4' }}>
                                    This ad is currently targeted to: <strong>{activeAd.state_name ? `${activeAd.city_name || 'All Cities'}, ${activeAd.state_name}` : 'Global'}</strong>.<br/>
                                    You can manually force this ad to appear (Include) or not appear (Exclude) for specific restaurants below.
                                </p>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
                                    <select 
                                        className="sa-select" 
                                        value={overrideRestId} 
                                        onChange={e => setOverrideRestId(e.target.value)}
                                        style={{ width: '100%' }}
                                    >
                                        <option value="">Select Restaurant...</option>
                                        {restaurants.map(r => (
                                            <option key={r.id} value={r.id}>{r.restaurant_name} ({r.subscription_tier || 'Trial'})</option>
                                        ))}
                                    </select>
                                    <select 
                                        className="sa-select" 
                                        value={overrideType} 
                                        onChange={e => setOverrideType(e.target.value)} 
                                        style={{ width: '110px' }}
                                    >
                                        <option value="Include">Include</option>
                                        <option value="Exclude">Exclude</option>
                                    </select>
                                    <button onClick={handleAddOverride} className="sa-primary-btn" style={{ padding: '0.4rem 1rem' }}>Add</button>
                                </div>

                                {activeAd.overrides && activeAd.overrides.length > 0 && (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                                        {activeAd.overrides.map(o => (
                                            <li key={o.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', background: 'var(--bg-card)' }}>
                                                <span>
                                                    <span style={{ color: o.override_type === 'Include' ? 'var(--success)' : 'var(--danger)', fontWeight: 'bold', marginRight: '6px' }}>[{o.override_type}]</span> 
                                                    <span style={{ color: 'var(--text-primary)' }}>{o.restaurant_name}</span>
                                                </span>
                                                <span 
                                                    style={{ color: 'var(--danger)', cursor: 'pointer', fontWeight: 600 }} 
                                                    onClick={() => handleRemoveOverride(o.restaurant_id)}
                                                >
                                                    <i className="fas fa-trash-alt" style={{ marginRight: '4px' }}></i>Remove
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--bg-dark)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '1rem' }}>Currently Running Locations</h4>
                                {locationsLoading ? (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Calculating live locations...</div>
                                ) : runningLocations.length === 0 ? (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>This ad is not currently active in any restaurants.</div>
                                ) : (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                                        {runningLocations.map((loc, idx) => (
                                            <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: idx < runningLocations.length - 1 ? '1px solid var(--border-color)' : 'none', fontSize: '0.85rem', background: 'var(--bg-card)' }}>
                                                <div>
                                                    <strong style={{ color: 'var(--text-primary)', display: 'block' }}>{loc.restaurant_name}</strong>
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}><i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>{loc.location}</span>
                                                </div>
                                                <span style={{ fontSize: '0.75rem', background: 'var(--bg-dark)', padding: '3px 8px', borderRadius: '12px', color: 'var(--text-secondary)' }}>{loc.plan}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {activeAd.status === 'Pending_Approval' && (
                                <div style={{ padding: '15px', border: '1px solid var(--success)', borderRadius: '8px', background: 'rgba(34,197,94,0.05)', marginBottom: '20px' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: 'var(--success)', fontSize: '1rem' }}>Approve & Set Pricing</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px', marginBottom: '15px' }}>
                                        <div className="form-group">
                                            <label style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Cost Per Click (₹) CPC</label>
                                            <input 
                                                type="number" 
                                                className="sa-input" 
                                                value={cpcRate} 
                                                onChange={e => setCpcRate(e.target.value)} 
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>Cost Per 1k Views (₹) CPM</label>
                                            <input 
                                                type="number" 
                                                className="sa-input" 
                                                value={cpmRate} 
                                                onChange={e => setCpmRate(e.target.value)} 
                                            />
                                        </div>
                                    </div>
                                    <button onClick={handleApprove} className="sa-primary-btn" style={{ width: '100%', padding: '10px' }}>Approve Ad & Go Live</button>
                                </div>
                            )}

                            <button onClick={() => setActiveAd(null)} className="sa-secondary-btn" style={{ width: '100%', padding: '10px' }}>Close Window</button>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default ManageAds;
